import { useMemo } from "react";

import { observer } from "mobx-react-lite";

import { Row } from "@/components/expression/inline-node/Row";
import {
  applyComputedDimming,
  groupNodesWithAttachments,
  useItemWidths,
} from "@/components/expression/inline-node/utils";
import { createWrappedRows } from "@/components/expression/inline-node/wrapping/createWrappedRows";
import { useNodeStyle } from "@/components/expression/utils/NodeStyleProvider";
import { twMerge } from "@/utils/twMerge";

import type {
  InlineNodeComponentProps,
  InlineNodeContentRenderItem,
} from "@/components/expression/inline-node/types";
import type { RowNode } from "@/components/expression/inline-node/wrapping/types";

export const MultiRowLayout = observer(function MultiRowLayout({
  node,
  doc,
  nodes,
  availableWidth,
  className,
  enableIndentation,
}: InlineNodeComponentProps) {
  const { widths, updateWidth } = useItemWidths();
  const { renderContext, computedSplit } = useNodeStyle();
  const items = useMemo(
    () => applyComputedDimming(groupNodesWithAttachments(nodes), computedSplit, renderContext),
    [nodes, computedSplit, renderContext],
  );

  const rows = createWrappedRows(items, availableWidth, widths, {
    enableIndentation,
    topLevelInline: node,
  });

  return (
    <>
      {rows.map((row, rowIndex) => {
        const rowKey = computeRowKey(row.content, rowIndex);
        const isLastRow = rowIndex === rows.length - 1;
        const splitBy = rows[rowIndex + 1]?.content
          ? findFirstItemInContent(rows[rowIndex + 1].content)
          : null;

        return (
          <Row
            key={rowKey}
            node={node}
            doc={doc}
            content={row.content}
            isFirstRow={rowIndex === 0}
            isLastRow={isLastRow}
            withFillers={isLastRow}
            onItemSize={updateWidth}
            splitBy={splitBy}
            indentation={row.indentation}
            rowId={`${node?.id ?? "inline"}-row-${rowIndex}`}
            className={twMerge(rows.length > 1 && "my-1", className)}
          />
        );
      })}
    </>
  );
});

function computeRowKey(content: RowNode[], rowIndex: number): string {
  for (const rowNode of content) {
    if (rowNode.kind === "items" && rowNode.items.length > 0) {
      return rowNode.items[0].key;
    }

    if (rowNode.kind === "group") {
      return `${rowNode.container.id}-${rowNode.slice}-${rowIndex}`;
    }
  }

  return `empty-row-${rowIndex}`;
}

/**
 * Finds the first `items` RowNode in the tree to use as the `splitBy` hint
 * (ghosted preview of where the next row begins). Returns the first item of
 * that run; nested group fragments are ignored.
 */
function findFirstItemInContent(content: RowNode[]): InlineNodeContentRenderItem | null {
  for (const node of content) {
    if (node.kind === "items" && node.items.length > 0) {
      return node.items[0];
    }

    if (node.kind === "group") {
      const inner = findFirstItemInContent(node.children);
      if (inner) return inner;
    }
  }

  return null;
}
